import type {
  BillingPlan,
  BillingProviderAdapter,
  Entitlement,
  Invoice,
  SubscriptionStatus,
} from "../types";

type DemoAdapterConfig = {
  apiBaseUrl?: string;
  getAuthToken?: () => string | null;
};

async function demoFetch<T>(
  apiBaseUrl: string,
  path: string,
  init?: RequestInit,
  getAuthToken?: () => string | null
): Promise<T> {
  const token = getAuthToken?.();
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "x-session-token": token } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Demo adapter request failed: ${response.status}`);
  }

  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Response shapes from the payment API routes
// ---------------------------------------------------------------------------

type PlansResponse = {
  ok: boolean;
  plans: Array<{
    id: string;
    name: string;
    description: string | null;
    status: string;
    featureList: string[];
    prices: Array<{
      id: string;
      amountCents: number;
      currency: string;
      interval: string;
    }>;
  }>;
};

type SubscriptionResponse = {
  ok: boolean;
  subscription: {
    id: string;
    customerId: string;
    planId: string;
    priceId: string;
    status: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  } | null;
};

type CheckoutResponse = {
  ok: boolean;
  checkoutUrl: string;
  sessionId: string;
};

type PortalResponse = {
  ok: boolean;
  portalUrl: string;
};

// ---------------------------------------------------------------------------
// Helpers to map API responses → UI billing types
// ---------------------------------------------------------------------------

function formatCents(amountCents: number, currency: string): string {
  const dollars = (amountCents / 100).toFixed(2);
  const symbol = currency.toUpperCase() === "USD" ? "$" : currency.toUpperCase() + " ";
  return `${symbol}${dollars}`;
}

function mapPlanToBillingPlan(plan: PlansResponse["plans"][number]): BillingPlan {
  const price = plan.prices[0];
  const priceLabel = price
    ? `${formatCents(price.amountCents, price.currency)}/${price.interval === "one-time" ? "" : price.interval === "year" ? "yr" : "mo"}`
    : "Free";
  return {
    id: plan.id,
    name: plan.name,
    priceLabel,
    interval: (price?.interval as BillingPlan["interval"]) ?? "month",
    description: plan.description ?? undefined,
    featureList: plan.featureList,
  };
}

function mapSubscriptionToStatus(sub: SubscriptionResponse["subscription"]): SubscriptionStatus {
  if (!sub) {
    return {
      active: false,
      status: "unknown",
      currentPeriodEnd: null,
      planId: null,
    };
  }
  const status = sub.status as SubscriptionStatus["status"];
  return {
    active: status === "active" || status === "trialing",
    status,
    currentPeriodEnd: sub.currentPeriodEnd,
    planId: sub.planId,
    cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
  };
}

// ---------------------------------------------------------------------------
// createDemoAdapter
// ---------------------------------------------------------------------------

export function createDemoAdapter(config: DemoAdapterConfig = {}): BillingProviderAdapter {
  const { apiBaseUrl = "", getAuthToken } = config;

  return {
    name: "demo",
    capabilities: {
      inlinePortal: false,
      invoices: false,
      paymentMethods: false,
    },

    async getPlans(): Promise<BillingPlan[]> {
      const data = await demoFetch<PlansResponse>(
        apiBaseUrl,
        "/api/website/payments/plans",
        undefined,
        getAuthToken
      );
      return data.plans.map(mapPlanToBillingPlan);
    },

    async getCurrentEntitlement(_userId: string): Promise<Entitlement> {
      // The demo payment backend doesn't have a dedicated entitlement endpoint,
      // so we derive entitlements from the subscription status.
      const data = await demoFetch<SubscriptionResponse>(
        apiBaseUrl,
        "/api/website/payments/subscription",
        undefined,
        getAuthToken
      );
      const sub = data.subscription;
      if (!sub || sub.status === "canceled") {
        return {
          tier: "free",
          features: { automations: false, projects: 1 },
          limits: { members: 1 },
          expiresAt: null,
        };
      }
      // Map planId to tier — we rely on plan name conventions from seed data
      const tier = sub.planId; // will be resolved by the consumer
      return {
        tier,
        features: { automations: true, projects: tier === "enterprise" ? 9999 : 100 },
        limits: { members: tier === "enterprise" ? 500 : 25 },
        expiresAt: sub.currentPeriodEnd,
      };
    },

    async startCheckout(planId: string, returnUrl: string) {
      // We need a priceId — fetch plans first to find it
      const plansData = await demoFetch<PlansResponse>(
        apiBaseUrl,
        "/api/website/payments/plans",
        undefined,
        getAuthToken
      );
      const plan = plansData.plans.find((p) => p.id === planId);
      const priceId = plan?.prices[0]?.id;
      if (!priceId) {
        // Fallback: use planId as priceId
        throw new Error(`No price found for plan ${planId}`);
      }

      const data = await demoFetch<CheckoutResponse>(
        apiBaseUrl,
        "/api/website/payments/checkout",
        {
          method: "POST",
          body: JSON.stringify({
            priceId,
            successUrl: returnUrl,
            cancelUrl: returnUrl,
          }),
        },
        getAuthToken
      );
      return { checkoutUrl: data.checkoutUrl };
    },

    async openCustomerPortal(returnUrl: string) {
      const data = await demoFetch<PortalResponse>(
        apiBaseUrl,
        "/api/website/payments/portal",
        {
          method: "POST",
          body: JSON.stringify({ returnUrl }),
        },
        getAuthToken
      );
      return { portalUrl: data.portalUrl };
    },

    async handleCallback() {
      return { ok: true, message: "Demo callback handled." };
    },

    async getSubscriptionStatus(_userId: string): Promise<SubscriptionStatus> {
      const data = await demoFetch<SubscriptionResponse>(
        apiBaseUrl,
        "/api/website/payments/subscription",
        undefined,
        getAuthToken
      );
      return mapSubscriptionToStatus(data.subscription);
    },

    async getInvoices(_userId: string): Promise<Invoice[]> {
      // The demo backend doesn't expose a website-level invoices endpoint yet,
      // so return an empty list rather than failing.
      return [];
    },
  };
}
